/*
 * @Author: yuhaiqin@wps.cn
 * @Description: 单实例弹出层
 */
import { createApp, h } from 'vue';
import './style.scss';

const POPUP_ID = 'ui-popup';
const POPUP_CONTAINER_ID = 'ui-popup-container';
let instance: any = null;

class Popup {
  vm: any;
  dom: any;
  el: any;
  change: any;
  constructor() {
    this.vm = null;
    this.dom = null;
    this.change = () => {
      console.log();
    };
  }

  /**
   * @description: 初始化弹出层
   * @param {*}
   * @return {*}
   */
  init({ component, offset, params, el, change, zIndex, placement, config }: any) {
    this.change = change || this.change;
    this.el = el;
    this._renderPopup({ component, params });
    this._setPosition({ el, offset, zIndex, placement, config });
    this._initListeners();
    this.change(true);
  }

  /**
   * @description: 移除 popup 元素
   * @param {*}
   * @return {*}
   */
  remove() {
    const container = document.getElementById(POPUP_CONTAINER_ID);
    if (!container) {
      return;
    }
    document.body.removeChild(container);
    this.dom = null;
    this.vm?.$destroy();
    this.vm = null;
    this.change(false);
    this._removeListeners();
  }

  /**
   * @description: 初始化监听点击事件
   * @param {*}
   * @return {*}
   */
  _initListeners() {
    // 延迟是为了不监听目标元素点击事件
    setTimeout(() => {
      document.addEventListener('click', clickEvent);
      document.addEventListener('scroll', scrollEvent, true);
    }, 10);
  }

  /**
   * @description: 移除监听点击事件
   * @param {*}
   * @return {*}
   */
  _removeListeners() {
    document.removeEventListener('click', clickEvent);
    document.removeEventListener('scroll', scrollEvent, true);
  }

  /**
   * @description: 创建 popup
   * @param {*}
   * @return {*}
   */
  _renderPopup({ component, params }: any) {
    // 创建一个容器来装载传输列表
    const container = document.createElement('div');
    container.id = POPUP_CONTAINER_ID;
    document.body.appendChild(container);

    // 创建实例，挂载界面
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this;
    const app = createApp({
      render() {
        return h(
          'div',
          {
            id: POPUP_ID,
          },
          [h(component || 'div', { remove: _this.remove.bind(_this), ...params })]
        );
      },
    });
    const vm: any = app.mount(container);
    this.dom = vm.$el;
  }

  /**
   * @description: 设置 popup 样式
   * @param {object} style 待设置样式
   * @return {*}
   */
  _setPopupStyle(style: any) {
    Object.keys(style).forEach((key) => {
      this.dom.style[key] = style[key];
    });
  }

  /**
   * @description: 设置 popup 位置
   * @param {number} offset 位移偏差
   * @param {object} el dom
   * @return {*}
   */
  _setPosition({ el, offset, zIndex, placement, config }: any) {
    // 获取触发点击的元素大小以及位置
    const targetRect = el.getBoundingClientRect();

    // 获取弹出层的元素大小以及位置
    const popup = document.getElementById(POPUP_ID)?.getBoundingClientRect() || { width: 0, height: 0 };

    // 获取当前视口的宽高
    const clientHeight = document.documentElement.clientHeight;
    const clientWidth = document.documentElement.clientWidth;

    // 判断当前 dom 在页面的渲染空间
    const { top: refTop, bottom: refBottom, left: refLeft, right: refRight } = targetRect;
    const isBottomEnough = popup.height + refBottom + offset < clientHeight;
    const isTopEnough = popup.height + offset < refTop;
    const isLeftEnough = popup.width < refRight;
    const isRightEnough = popup.width + refLeft < clientWidth;

    // 根据传入 placement 精准计算最终定位
    let top = 0;
    let left = 0;
    const topPosition = refTop - popup.height - offset;
    const botPosition = refBottom + offset;
    const rightPosition = refRight - popup.width;
    if (placement.includes('bottom')) {
      top = isBottomEnough ? botPosition : topPosition;
    } else {
      top = isTopEnough ? topPosition : botPosition;
    }
    if (placement.includes('start')) {
      left = isRightEnough ? refLeft : rightPosition;
    } else {
      left = isLeftEnough ? rightPosition : refLeft;
    }

    // 与触发元素水平居中
    if (['bottom', 'top'].includes(placement)) {
      // 判断是否超出屏幕最左侧
      const defaultLeft = Math.round(refLeft - popup.width / 2);
      left = defaultLeft < 0 ? config.minLeft || 0 : defaultLeft;
      // 判断是否超出屏幕最右侧
      if (left + popup.width > clientWidth) {
        left = clientWidth - popup.width;
      }
    }

    // 设置弹出层位置样式
    const style = {
      top: `${top}px`,
      left: `${left}px`,
      zIndex: zIndex,
    };
    this._setPopupStyle(style);
  }
}

/**
 * @description: 实现点击空白处关闭弹出层逻辑
 * @param {object} e 点击元素
 * @return {*}
 */
function clickEvent(e: any) {
  // 判断点击的元素是否弹出层内容
  if (instance && instance.dom.contains(e.target)) {
    return;
  }
  instance && instance.remove();
}

/**
 * @description: 实现页面滚动关闭弹出层逻辑
 * @param {object} e 滚动容器
 * @return {*}
 */
function scrollEvent(e: any) {
  // 当前滚动容器为触发弹出层的元素
  if (instance && e.target === instance.el) {
    return;
  }

  // 当前滚动容器为触发元素的父级容器
  if (instance && e.target.contains(instance.el)) {
    instance.remove();
  }
}

/**
 * @description: 创建一个弹出层实例
 * @param {Function} change 弹出层显隐切换事件
 * @param {Object} el 触发元素
 * @param {Object} component 弹出层展示的组件
 * @param {Object} params 需要透传的参数
 * @param {Number} offset 弹出层距离触发元素的距离
 * @param {Number} zIndex 样式层级
 * @param {String} placement 支持的位置 bottom | top | bottom-start | bottom-end | top-start | top-end
 * @param {Object} config 其他配置 { minLeft: 16 }
 * @return {*}
 */
export const newPopup = ({
  change,
  el,
  component,
  params = {},
  offset = 4,
  zIndex = 2000,
  placement = 'bottom-start',
  config = {},
}: any) => {
  instance && instance.remove();
  instance = new Popup();
  instance.init({ component, params, offset, el, change, zIndex, placement, config });
  return instance;
};
